// components/admin/accounting/DriversAccounting.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Truck, Loader2 } from 'lucide-react';
import DeliveriesTable from './DeliveriesTable';
import StatisticsCards from './StatisticsCards';

const API_URL = process.env.REACT_APP_API_URL;

function DriversAccounting({ userId, selectedDate }) {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDrivers();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDriver) {
      fetchDriverDeliveries(selectedDriver.id, '');
    }
  }, [selectedDriver, selectedDate]);

  async function fetchDrivers() {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/accounting/drivers`, {
        params: { user_id: userId, date: selectedDate }
      });
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDriverDeliveries(driverId, search) {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/accounting/driver/${driverId}/deliveries`,
        {
          params: { admin_id: userId, date: selectedDate, search }
        }
      );
      setDeliveries(response.data.deliveries);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleDriverSelect(driver) {
    setSelectedDriver(driver);
    setSearchTerm('');
  }

  function handleSearch(search) {
    if (selectedDriver) {
      fetchDriverDeliveries(selectedDriver.id, search);
    }
  }

  function handleExport() {
    const data = {
      admin_id: userId,
      deliveries: deliveries,
      statistics: statistics,
      type: 'driver',
      title: `Contabilidad - ${selectedDriver?.username || 'Conductor'} - ${selectedDate}`
    };

    axios.post(`${API_URL}/api/accounting/export`, data, {
      responseType: 'blob'
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `contabilidad_${selectedDriver?.username}_${selectedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error('Error exporting:', error);
      });
  }

  if (!selectedDriver) {
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 font-montserrat mb-4">
            Selecciona un Conductor
          </h2>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-montserrat">
                No hay conductores con entregas en esta fecha
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {drivers.map((driver) => (
                <button
                  key={driver.id}
                  onClick={() => handleDriverSelect(driver)}
                  className="bg-white border-2 border-slate-200 rounded-xl p-4 hover:border-brand-primary hover:shadow-lg transition-all text-left group"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800 font-montserrat group-hover:text-brand-primary transition-colors">
                        {driver.username}
                      </h3>
                      <p className="text-sm text-slate-500">{driver.email}</p>
                    </div>
                    <div className="bg-brand-green/20 text-brand-green px-3 py-1 rounded-full text-sm font-bold font-montserrat">
                      {driver.delivery_count}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 font-montserrat">
                    {driver.phone_number}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Driver Header */}
      <div className="mb-6 bg-gradient-to-r from-brand-green to-brand-green/80 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold font-montserrat mb-1">
              {selectedDriver.username}
            </h2>
            <p className="text-white/90 text-sm">{selectedDriver.email}</p>
          </div>
          <button
            onClick={() => setSelectedDriver(null)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-montserrat font-semibold transition-all"
          >
            Volver
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <StatisticsCards statistics={statistics} type="driver" />
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre o teléfono..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-brand-primary transition-all font-montserrat"
          />
        </div>
      </div>

      {/* Deliveries Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : (
        <DeliveriesTable 
          deliveries={deliveries} 
          onExport={handleExport}
          showUserColumn={true}
        />
      )}
    </div>
  );
}

export default DriversAccounting;